import { queryHtmlElement } from "../core/dom";

const PUBLIC_SECTION_PATHS = [
  ["availability", "/availability"],
  ["equipment", "/equipment"],
  ["pricing", "/pricing"],
  ["signup", "/signup"],
  ["login", "/login"]
] as const;

/** Enhances the shared public header with location state and a narrow-screen menu. */
export class PublicNavigationController {
  private readonly button: HTMLButtonElement | null;
  private readonly header: HTMLElement | null;
  private readonly menu: HTMLElement | null;
  private readonly pathname: string;

  public constructor(root: ParentNode, pathname?: string) {
    this.header = queryHtmlElement(root, "[data-public-navigation]", HTMLElement);
    this.button = queryHtmlElement(root, "[data-public-menu-button]", HTMLButtonElement);
    this.menu = queryHtmlElement(root, "[data-public-menu]", HTMLElement);
    const browserWindow: { location?: { pathname?: string } } = window;
    this.pathname = pathname ?? browserWindow.location?.pathname ?? "/";
  }

  public connect(): void {
    if (this.header === null || this.button === null || this.menu === null) {
      return;
    }

    this.header.dataset.publicNavigationEnhanced = "true";
    this.markCurrentLocation(this.pathname);
    this.button.addEventListener("click", this.handleButtonClick);
    this.menu.addEventListener("click", this.handleMenuClick);
    document.addEventListener("keydown", this.handleDocumentKeydown);
  }

  private readonly handleButtonClick = (): void => {
    this.setOpen(this.button?.getAttribute("aria-expanded") !== "true");
  };

  private readonly handleDocumentKeydown = (event: KeyboardEvent): void => {
    if (event.key !== "Escape" || this.button?.getAttribute("aria-expanded") !== "true") {
      return;
    }

    this.setOpen(false);
    this.button.focus();
  };

  private readonly handleMenuClick = (event: MouseEvent): void => {
    if (event.target instanceof HTMLAnchorElement) {
      this.setOpen(false);
    }
  };

  private markCurrentLocation(pathname: string): void {
    const currentSection = PUBLIC_SECTION_PATHS.find(([, path]) =>
        pathname === path || pathname.startsWith(`${path}/`)
      )?.[0];
    if (currentSection === undefined) {
      return;
    }

    this.header?.querySelector<HTMLElement>(`[data-public-section="${currentSection}"]`)
      ?.setAttribute("aria-current", "page");
  }

  private setOpen(open: boolean): void {
    if (this.header !== null) {
      this.header.dataset.publicMenuOpen = open ? "true" : "false";
    }
    this.button?.setAttribute("aria-expanded", open ? "true" : "false");
  }
}
